import { Alignment, AnchorButton, Navbar, Position } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Tooltip, VoiceboxLogo } from 'vet-bones/components';
import { noop } from 'vet-bones/utils';

import { Rule } from 'src/components/icons/Rule';
import { TEST_IDS } from 'src/constants/testIds';
import { useRemoveSelection } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveSelection';
import { useDiagramSelection } from 'src/containers/ProjectDiagram/hooks/useDiagramSelectionValue';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  MappingField,
  ModelConceptClass,
  Resource,
} from 'src/containers/ProjectDiagram/types/data';
import {
  isModelConceptClass,
  isResource,
} from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { usePortalFeatures } from 'src/utils/hooks/usePortalFeatures';

type ProjectDiagramNavbarButtonProps = {
  disabled?: boolean;
  icon?: React.ComponentProps<typeof AnchorButton>['icon'];
  onClick?: React.ComponentProps<typeof AnchorButton>['onClick'];
  testId?: string;
  tooltipContent: React.ComponentProps<typeof Tooltip>['content'];
};

const ProjectDiagramNavbarButton: React.VFC<
  ProjectDiagramNavbarButtonProps
> = ({ disabled = false, icon, onClick = noop, testId, tooltipContent }) => (
  <Tooltip content={tooltipContent} position={Position.TOP}>
    <AnchorButton
      data-testid={testId}
      disabled={disabled}
      icon={icon}
      minimal
      onClick={onClick}
    />
  </Tooltip>
);

export const ProjectDiagramNavbar: React.VFC = () => {
  const diagram = useDiagram();
  const dispatch = useDispatch();
  const selection = useDiagramSelection();
  const handleDeleteSelection = useRemoveSelection();

  const portalFeatures = usePortalFeatures();

  const isVoiceboxButtonVisible = portalFeatures?.data?.voiceBox;

  // TODO instead of only disabling input field once dialog is open,
  // disable button and spin it until we're connected to stardog etc
  const isVoiceboxDisabled = false;

  const handleVoicebox = () => {
    dispatch(uiSlice.actions.toggledVoiceboxDialog({ isOpen: true }));
  };

  const handleAddClass = () => {
    dispatch(
      uiSlice.actions.toggledCreateClassDrawer({
        isOpen: true,
      })
    );
  };

  const isSelectionExactlyTwoClasses = React.useMemo(() => {
    if (selection.size !== 2) {
      return false;
    }
    let isAllClasses = true;
    selection.each((part) => {
      if (!isModelConceptClass(part.data)) {
        isAllClasses = false;
      }
    });
    return isAllClasses;
  }, [selection]);

  const isMappingEnabled = React.useMemo(
    () =>
      (selection.size === 1 && isResource(selection.first()?.data)) ||
      (selection.size === 2 &&
        selection.any((part) => isResource(part?.data)) &&
        selection.any((part) => isModelConceptClass(part?.data))),
    [selection]
  );

  // disable relationship button if there are more than 2 selections or if both selections are not classes
  const isRelationshipDisabled = React.useMemo(() => {
    if (selection.size > 2) {
      return true;
    }
    if (selection.size < 2) {
      const currentSelection = selection.first();
      if (currentSelection) {
        return !isModelConceptClass(currentSelection.data);
      }
      return false;
    }
    if (selection.size === 2) {
      return !isSelectionExactlyTwoClasses;
    }
    return false;
  }, [selection, isSelectionExactlyTwoClasses]);

  const handleAddRelationship = () => {
    if (!diagram) {
      return;
    }

    const currentSelectedItem = selection.size > 0 ? selection.first() : null;
    const selectedData = currentSelectedItem?.data;
    const modelDataSelection = diagram.model.modelData.selection;

    if (
      selection.size === 1 &&
      selectedData &&
      isModelConceptClass(selectedData)
    ) {
      dispatch(
        uiSlice.actions.toggledCreateRelationshipDrawer({
          isOpen: true,
          sourceClassIri: selectedData.value,
        })
      );
    } else if (
      modelDataSelection.length === 2 &&
      isSelectionExactlyTwoClasses
    ) {
      const firstSelection = getModelConceptClassValue(modelDataSelection[0]);
      const secondSelection = getModelConceptClassValue(modelDataSelection[1]);
      const sourceValue = firstSelection;
      const targetValue = secondSelection;

      dispatch(
        uiSlice.actions.toggledCreateRelationshipDrawer({
          isOpen: true,
          sourceClassIri: sourceValue,
          targetClassIri: targetValue,
        })
      );
    } else {
      dispatch(
        uiSlice.actions.toggledCreateRelationshipDrawer({
          isOpen: true,
        })
      );
    }
  };

  const handleRules = () => {
    dispatch(uiSlice.actions.toggledRuleOverviewDrawer({ isOpen: true }));
  };

  const handleAddMapping = () => {
    if (!diagram || selection.size > 2) {
      return;
    }

    let classData: ModelConceptClass | undefined;
    let resourceData: Resource | undefined;
    selection.each((part) => {
      if (!part.data) {
        return;
      }

      if (isModelConceptClass(part.data)) {
        classData = part.data;
      } else if (isResource(part.data)) {
        resourceData = part.data;
      }
    });

    if (resourceData) {
      const fields = resourceData.fields.reduce<Record<string, MappingField>>(
        (acc, { label, value }) => {
          acc[value] = { label, value, targets: [] };
          return acc;
        },
        {}
      );
      dispatch(
        uiSlice.actions.toggledCreateMappingDialog({
          isOpen: true,
          fromId: resourceData.value,
          fields,
          toValue: classData?.value,
        })
      );
    }
  };

  return (
    <Navbar className="sd-project-diagram-toolbar">
      <Navbar.Group align={Alignment.LEFT}>
        {isVoiceboxButtonVisible ? (
          <AnchorButton
            data-testid={TEST_IDS.projectDiagram.navbar.voicebox}
            disabled={isVoiceboxDisabled}
            icon={<VoiceboxLogo />}
            onClick={handleVoicebox}
            outlined
            small
            text={copy.components.projectDiagram.navbar.voicebox}
          />
        ) : null}
        <ProjectDiagramNavbarButton
          icon={IconNames.NEW_OBJECT}
          onClick={handleAddClass}
          testId={TEST_IDS.projectDiagram.navbar.addClass}
          tooltipContent={copy.components.projectDiagram.navbar.addClass}
        />
        <ProjectDiagramNavbarButton
          disabled={isRelationshipDisabled}
          icon={IconNames.NEW_LINK}
          onClick={handleAddRelationship}
          testId={TEST_IDS.projectDiagram.navbar.addRelationship}
          tooltipContent={copy.components.projectDiagram.navbar.addRelationship}
        />
        <ProjectDiagramNavbarButton
          disabled={!selection.size}
          icon={IconNames.TRASH}
          onClick={handleDeleteSelection}
          testId={TEST_IDS.projectDiagram.navbar.deleteSelection}
          tooltipContent={copy.components.projectDiagram.navbar.deleteSelection}
        />
        <ProjectDiagramNavbarButton
          disabled={!diagram?.undoManager.canUndo()}
          icon={IconNames.UNDO}
          onClick={() => diagram?.undoManager.undo()}
          testId={TEST_IDS.projectDiagram.navbar.undo}
          tooltipContent={copy.components.projectDiagram.navbar.undo}
        />
        <ProjectDiagramNavbarButton
          disabled={!diagram?.undoManager.canRedo()}
          icon={IconNames.REDO}
          onClick={() => diagram?.undoManager.redo()}
          testId={TEST_IDS.projectDiagram.navbar.redo}
          tooltipContent={copy.components.projectDiagram.navbar.redo}
        />
        <AnchorButton
          className="sd-project-diagram-toolbar--button"
          data-testid={TEST_IDS.projectDiagram.navbar.addMapping}
          disabled={!isMappingEnabled}
          icon={IconNames.PIVOT}
          onClick={handleAddMapping}
          outlined
          small
          text={copy.components.projectDiagram.navbar.addMapping}
        />
        <AnchorButton
          className="sd-project-diagram-toolbar--button"
          data-testid={TEST_IDS.projectDiagram.navbar.rules}
          icon={<Rule />}
          onClick={handleRules}
          outlined
          small
          text={copy.components.projectDiagram.navbar.rules}
        />
      </Navbar.Group>
    </Navbar>
  );
};
